import { showWarningNotification } from '../../../widgets/notificationToaster';
import { Dispatch, SetStateAction, useCallback, useMemo, useRef } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { commitLocalUpdate } from 'relay-runtime';
import { T } from 'src/shared/helpers';
import { AdOptions, InputImageData, VideoOptions } from '../types';
import { SelectionValue } from '../components/presets/presetOptionButtons';
import { TOOLS } from '../constants';
import aiStudioApi from '../tasks';
import { AssetGenerationSettings, GenerationResult, ProductSideImage } from '../types';
import { getSideImageAsBase64 } from '../utils/imageHelpers';
import { AIStudioState, updateToolState } from './useAIStudioState';
import { useGenerationPreflight } from './useGenerationPreflight';

interface UseAIStudioGenerationParams {
    state: AIStudioState;
    setState: Dispatch<SetStateAction<AIStudioState>>;
    inputImages: InputImageData[];
    setInputImages: Dispatch<SetStateAction<InputImageData[]>>;
    sideImages?: ProductSideImage[];
    selectedImageIndex: number;
    viewMode: 'images' | '3d';
    has3D: boolean;
    productId?: number;
    viewer3DCapture: { capture: () => Promise<string | null> } | null;
    videoOptions: VideoOptions;
    adOptions: AdOptions;
    productImageUrl: string | undefined;
    isInTrialOrTestStore: boolean;
    isAutoCapturing: boolean;
    onFairUseLimitReached?: (serviceName: string) => void;
    setIsProductInputEditing: Dispatch<SetStateAction<boolean>>;
    isProductInputEditing: boolean;
    hasProductInput: boolean;
}

// --- Selection helpers ---

function getSelectedPresetId(selections: Record<string, SelectionValue>, typeId: string): string | undefined {
    const selection = selections[typeId];
    if (Array.isArray(selection)) {
        return selection[0]?.id;
    }
    return selection?.id;
}

function getSelectedPresetIds(selections: Record<string, SelectionValue>, typeId: string): string[] {
    const selection = selections[typeId];
    if (Array.isArray(selection)) {
        return selection.map((s) => s.id);
    }
    return selection ? [selection.id] : [];
}

function getImageUrl(selections: Record<string, SelectionValue>, typeId: string): string | undefined {
    const selection = selections[typeId];
    if (Array.isArray(selection)) {
        return selection[0]?.thumbnailUrl;
    }
    return selection?.thumbnailUrl;
}

function getImageUrls(selections: Record<string, SelectionValue>, typeId: string): string[] {
    const selection = selections[typeId];
    if (Array.isArray(selection)) {
        return selection.map((s) => s.thumbnailUrl).filter((url): url is string => !!url);
    }
    return selection?.thumbnailUrl ? [selection.thumbnailUrl] : [];
}

function getSelectedFactor(selections: Record<string, SelectionValue>, typeId: string): number | undefined {
    const selection = selections[typeId];
    if (Array.isArray(selection)) {
        return selection[0]?.factor;
    }
    return selection?.factor;
}

export interface UseAIStudioGenerationReturn {
    handleGenerate: () => Promise<void>;
    handleStopGeneration: () => void;
    canGenerate: boolean;
    getGenerationSettings: () => AssetGenerationSettings;
}

export function useAIStudioGeneration({
    state,
    setState,
    inputImages,
    setInputImages,
    sideImages,
    selectedImageIndex,
    viewMode,
    has3D,
    viewer3DCapture,
    videoOptions,
    adOptions,
    productImageUrl,
    isInTrialOrTestStore,
    isAutoCapturing,
    onFairUseLimitReached,
    setIsProductInputEditing,
    isProductInputEditing,
    hasProductInput
}: UseAIStudioGenerationParams): UseAIStudioGenerationReturn {
    const relayEnvironment = useRelayEnvironment();
    // Per-tool cancellation flags: set when the user clicks Stop on a tool. Required because
    // the user can now switch tools while a generation runs (and even start parallel generations
    // in different tools) — a single shared ref would falsely cancel unrelated generations.
    const cancelledToolsRef = useRef<Set<string>>(new Set());
    const { runWithPreflight } = useGenerationPreflight();

    // --- canGenerate ---
    const canGenerate = useMemo(() => {
        if (isAutoCapturing) return false;

        const hasInputImage = inputImages.length > 0 || !!productImageUrl || (has3D && viewMode === '3d');

        switch (state.selectedTool) {
            case 'virtual-tryon': {
                const hasModel = !!getSelectedPresetId(state.selections, 'model') || !!getImageUrl(state.selections, 'model');
                return hasModel && hasInputImage;
            }
            case 'product-staging':
            case 'magic-swap':
            case 'multi-image-fusion':
                return hasInputImage;
            case 'ad-generator': {
                const hasContent = !!adOptions.title || !!state.prompt;
                return hasInputImage && hasContent;
            }
            case 'video-generation': {
                const hasAnimationOrPrompt = !!getSelectedPresetId(state.selections, 'animation') || !!state.prompt;
                return hasInputImage && hasAnimationOrPrompt;
            }
            case 'upscaler':
                return hasInputImage;
            case 'text-to-3d':
                return !!state.prompt;
            case 'image-to-3d':
                return hasInputImage;
            default:
                return hasInputImage;
        }
    }, [inputImages, productImageUrl, has3D, viewMode, state.selectedTool, state.prompt, state.selections, adOptions]);

    // --- populateInputImages: resolves 3D capture or side image into inputImages ---
    const populateInputImages = useCallback(
        async (skipInputCheck: boolean): Promise<InputImageData[]> => {
            // Keep user-uploaded (non-product) images when re-capturing product input
            const userImages = inputImages.filter((img) => !img.fromProduct);

            // In 3D mode, always re-capture (user may have rotated the model)
            if (viewMode === '3d' && viewer3DCapture) {
                const base64 = await viewer3DCapture.capture();
                if (base64) {
                    const productImage: InputImageData = { id: `input-3d-${Date.now()}`, url: base64, name: '3D Capture', fromProduct: true };
                    const newImages = [productImage, ...userImages];
                    setInputImages(newImages);
                    return newImages;
                }
            }
            if (inputImages.length > 0 && !skipInputCheck) {
                return inputImages;
            }
            if (sideImages && sideImages.length > 0) {
                const index = Math.min(selectedImageIndex, sideImages.length - 1);
                const sideImage = sideImages[index];
                const url = await getSideImageAsBase64(sideImage);
                if (url) {
                    const productImage: InputImageData = { id: `input-side-${Date.now()}`, url, name: 'Product image', fromProduct: true };
                    const newImages = [productImage, ...userImages];
                    setInputImages(newImages);
                    return newImages;
                }
            }
            return [];
        },
        [inputImages, viewMode, viewer3DCapture, sideImages, selectedImageIndex, setInputImages]
    );

    // --- getInputImageUrls ---
    const getInputImageUrls = useCallback((): string[] => {
        if (inputImages.length > 0) {
            return inputImages.map((img) => img.url);
        }
        return [];
    }, [inputImages]);

    // --- executeGeneration (actual generation pipeline; gated by handleGenerate) ---
    const executeGeneration = useCallback(async () => {
        if (!canGenerate) return;

        if (isInTrialOrTestStore) {
            showWarningNotification({
                message: T._(
                    'AI Agents Studio is not available during the trial period or for Shopify test stores. Upgrade your plan or use a real store to access AI Agents Studio.',
                    'Admin'
                )
            });
            return;
        }

        // Capture the tool that initiated this generation: state.selectedTool may change
        // while we await the API (the user can switch to another tool from the header).
        // All subsequent toolStates updates must target the originating tool.
        const initiatingTool = state.selectedTool;
        if (!initiatingTool) return;

        // Capture the start timestamp so we can identify this specific generation.
        // If a later generation in the same tool replaces this one's toolState before
        // our API call resolves, the timestamps won't match and we drop the stale result.
        const generationStartedAt = Date.now();

        // Reset cancellation for this tool only (preserves any other tool's cancel state)
        cancelledToolsRef.current.delete(initiatingTool);
        setState((prev) => ({ ...updateToolState(prev, initiatingTool, { isGenerating: true, generationStartedAt }), error: null }));

        try {
            // Now safely switch to gallery view
            setIsProductInputEditing(false);
            setState((prev) => updateToolState(prev, initiatingTool, { view: 'gallery' }));

            // Clear input images when generating from the 2D/3D editor so populateInputImages re-captures fresh
            const skipInputCheck = isProductInputEditing && hasProductInput;
            if (skipInputCheck) setInputImages((prev) => prev.filter((img) => !img.fromProduct));

            const resolvedImages = await populateInputImages(skipInputCheck);
            const preparedProductUrl = resolvedImages[0]?.url;

            let result: GenerationResult;
            const { selections, prompt } = state;

            switch (state.selectedTool) {
                case 'virtual-tryon': {
                    const modelId = getSelectedPresetId(selections, 'model');
                    const isModelCustom = modelId?.startsWith('custom-');
                    const modelImageUrl = isModelCustom ? getImageUrl(selections, 'model') : undefined;
                    const modelPreset = !isModelCustom ? modelId : undefined;

                    const vtoBgId = getSelectedPresetId(selections, 'background');
                    const isVtoBgCustom = vtoBgId?.startsWith('custom-');
                    const vtoBackgroundImageUrl = isVtoBgCustom ? getImageUrl(selections, 'background') : undefined;
                    const vtoBackgroundPreset = !isVtoBgCustom ? vtoBgId : undefined;

                    const clothingImageUrl = preparedProductUrl;
                    if (!clothingImageUrl) throw new Error('Clothing image required');

                    result = await aiStudioApi.virtualTryOn({
                        personImageUrl: modelImageUrl,
                        personPreset: modelPreset,
                        clothingImageUrl,
                        backgroundImageUrl: vtoBackgroundImageUrl,
                        backgroundPreset: vtoBackgroundPreset,
                        posePreset: getSelectedPresetId(selections, 'pose'),
                        effectPreset: getSelectedPresetId(selections, 'effect'),
                        customPrompt: prompt || undefined,
                        saveToLibrary: true
                    });
                    break;
                }
                case 'product-staging': {
                    const productUrl = preparedProductUrl;
                    if (!productUrl) throw new Error('Product image required');

                    const bgId = getSelectedPresetId(selections, 'background');
                    const backgroundImageUrl = getImageUrl(selections, 'background');
                    const backgroundPreset = bgId;

                    result = await aiStudioApi.productStaging({
                        productImageUrl: productUrl,
                        backgroundImageUrl,
                        backgroundPreset,
                        surfacePreset: getSelectedPresetId(selections, 'surface'),
                        moodPreset: getSelectedPresetId(selections, 'mood'),
                        effectPreset: getSelectedPresetId(selections, 'effect'),
                        customPrompt: prompt || undefined,
                        saveToLibrary: true
                    });
                    break;
                }
                case 'magic-swap': {
                    const productUrl = preparedProductUrl;
                    if (!productUrl) throw new Error('Product image required');

                    const materialId = getSelectedPresetId(selections, 'material');
                    const isMaterialCustom = materialId?.startsWith('custom-');
                    const materialImageUrl = isMaterialCustom ? getImageUrl(selections, 'material') : undefined;
                    const materialPreset = !isMaterialCustom ? materialId : undefined;

                    const colorId = getSelectedPresetId(selections, 'color');
                    const isColorCustom = colorId?.startsWith('custom-');
                    const colorImageUrl = isColorCustom ? getImageUrl(selections, 'color') : undefined;
                    const colorPreset = !isColorCustom ? colorId : undefined;

                    result = await aiStudioApi.magicSwap({
                        productImageUrl: productUrl,
                        materialPreset,
                        materialImageUrl,
                        colorPreset,
                        colorImageUrl,
                        effectPreset: getSelectedPresetId(selections, 'effect'),
                        customPrompt: prompt || undefined,
                        saveToLibrary: true
                    });
                    break;
                }
                case 'multi-image-fusion': {
                    let imageUrls = getInputImageUrls();
                    if (imageUrls.length === 0 && preparedProductUrl) {
                        imageUrls = [preparedProductUrl];
                    }
                    if (imageUrls.length === 0) throw new Error('Images required');

                    result = await aiStudioApi.multiImageFusion({
                        productImageUrls: imageUrls,
                        layoutPreset: getSelectedPresetId(selections, 'layout'),
                        moodPreset: getSelectedPresetId(selections, 'mood'),
                        effectPreset: getSelectedPresetId(selections, 'effect'),
                        customPrompt: prompt || undefined,
                        saveToLibrary: true
                    });
                    break;
                }
                case 'ad-generator': {
                    const productUrl = preparedProductUrl;
                    if (!productUrl) throw new Error('Product image required');

                    result = await aiStudioApi.adGenerator({
                        productImageUrl: productUrl,
                        formatPreset: getSelectedPresetId(selections, 'format'),
                        stylePreset: getSelectedPresetId(selections, 'style'),
                        effectPreset: getSelectedPresetId(selections, 'effect'),
                        language: adOptions.language,
                        title: adOptions.title || undefined,
                        subtitle: adOptions.subtitle || undefined,
                        ctaText: adOptions.ctaText || undefined,
                        customPrompt: prompt || undefined,
                        saveToLibrary: true
                    });
                    break;
                }
                case 'video-generation': {
                    const imageUrl = preparedProductUrl;
                    if (!imageUrl) throw new Error('Image required');

                    const videoResult = await aiStudioApi.videoGeneration({
                        imageUrl,
                        durationSeconds: videoOptions.duration,
                        model: videoOptions.model,
                        resolution: videoOptions.resolution,
                        aspectRatio: videoOptions.aspectRatio,
                        generateAudio: videoOptions.generateAudio,
                        videoPreset: getSelectedPresetId(selections, 'animation'),
                        effectPreset: getSelectedPresetId(selections, 'effect'),
                        customPrompt: prompt || undefined,
                        saveToLibrary: true
                    });
                    result = videoResult;
                    break;
                }
                case 'upscaler': {
                    const imageUrl = preparedProductUrl;
                    if (!imageUrl) throw new Error('Image required');

                    const factorValue = getSelectedFactor(selections, 'factor');
                    let scaleFactor: 2 | 3 | 4 = 2;
                    if (factorValue === 2 || factorValue === 3 || factorValue === 4) {
                        scaleFactor = factorValue;
                    }
                    result = await aiStudioApi.upscaler({
                        imageUrl,
                        scaleFactor,
                        enableCreativity: false,
                        saveToLibrary: true
                    });
                    break;
                }
                case 'text-to-3d': {
                    if (!prompt) throw new Error('Text prompt required');
                    result = await aiStudioApi.modelGeneration3D(
                        {
                            mode: 'text-to-3d',
                            prompt,
                            faceCount: 50000,
                            enablePBR: true,
                            saveToLibrary: true
                        },
                        'text-to-3d'
                    );
                    break;
                }
                case 'image-to-3d': {
                    const imageUrl = preparedProductUrl;
                    if (!imageUrl) throw new Error('Image required');
                    result = await aiStudioApi.modelGeneration3D(
                        {
                            mode: 'image-to-3d',
                            imageUrl,
                            faceCount: 50000,
                            enablePBR: true,
                            saveToLibrary: true
                        },
                        'image-to-3d'
                    );
                    break;
                }
                default:
                    throw new Error(`Unknown tool: ${state.selectedTool}`);
            }

            if (cancelledToolsRef.current.has(initiatingTool)) {
                cancelledToolsRef.current.delete(initiatingTool);
                return;
            }

            setState((prev) => {
                const toolState = prev.toolStates[initiatingTool];
                // Drop the result if: (a) the tool was reset (user pressed Home), (b) its generation
                // was already stopped, or (c) a newer generation has replaced ours in the same tool.
                if (!toolState || !toolState.isGenerating || toolState.generationStartedAt !== generationStartedAt) return prev;
                return updateToolState(prev, initiatingTool, {
                    isGenerating: false,
                    generationStartedAt: null,
                    results: [result, ...toolState.results].slice(0, 10)
                });
            });

            // Invalidate Relay store so asset library refetches
            commitLocalUpdate(relayEnvironment, (store) => {
                store.invalidateStore();
            });
        } catch (error) {
            if (cancelledToolsRef.current.has(initiatingTool)) {
                cancelledToolsRef.current.delete(initiatingTool);
                return;
            }
            console.error('Generation failed:', error);
            const errorMessage = error instanceof Error ? error.message : 'Generation failed';
            setState((prev) => {
                const toolState = prev.toolStates[initiatingTool];
                // Suppress the error if a newer generation is now active for this tool — the
                // failure belongs to a stale request the user is no longer waiting on.
                if (!toolState || toolState.generationStartedAt !== generationStartedAt) return prev;
                return {
                    ...updateToolState(prev, initiatingTool, { isGenerating: false, generationStartedAt: null }),
                    error: errorMessage
                };
            });
        }
    }, [
        canGenerate,
        isInTrialOrTestStore,
        isProductInputEditing,
        hasProductInput,
        setInputImages,
        getInputImageUrls,
        state.selectedTool,
        state.prompt,
        state.selections,
        videoOptions,
        adOptions,
        relayEnvironment,
        setState
    ]);

    // --- handleGenerate: runs the generation pipeline gated by addon activation + service availability ---
    const handleGenerate = useCallback(async () => {
        if (!state.selectedTool) return;
        await runWithPreflight(state.selectedTool, executeGeneration, {
            onLimitReached: (serviceName) => onFairUseLimitReached?.(serviceName)
        });
    }, [runWithPreflight, state.selectedTool, executeGeneration, onFairUseLimitReached]);

    const handleStopGeneration = useCallback(() => {
        setState((prev) => {
            if (!prev.selectedTool) return prev;
            cancelledToolsRef.current.add(prev.selectedTool);
            return updateToolState(prev, prev.selectedTool, { isGenerating: false, generationStartedAt: null });
        });
    }, [setState]);

    const getGenerationSettings = useCallback((): AssetGenerationSettings => {
        const { selections, prompt, selectedTool } = state;
        const toolConfig = TOOLS.find((t) => t.id === selectedTool);

        const settings: AssetGenerationSettings = {
            toolId: selectedTool || undefined,
            toolName: toolConfig?.name,
            prompt: prompt || undefined,

            // Map preset selections to named fields
            backgroundPreset: getSelectedPresetId(selections, 'background'),
            backgroundImageUrl: getImageUrl(selections, 'background'),
            personPreset: getSelectedPresetId(selections, 'model'),
            personImageUrl: getImageUrl(selections, 'model'),
            clothingPreset: getSelectedPresetId(selections, 'clothing'),
            clothingImageUrl: getImageUrl(selections, 'clothing'),
            posePreset: getSelectedPresetId(selections, 'pose'),
            effectPreset: getSelectedPresetId(selections, 'effect'),
            surfacePreset: getSelectedPresetId(selections, 'surface'),
            moodPreset: getSelectedPresetId(selections, 'mood'),
            materialPreset: getSelectedPresetId(selections, 'material'),
            colorPreset: getSelectedPresetId(selections, 'color'),
            layoutPreset: getSelectedPresetId(selections, 'layout'),
            formatPreset: getSelectedPresetId(selections, 'format'),
            stylePreset: getSelectedPresetId(selections, 'style'),
            videoPreset: getSelectedPresetId(selections, 'animation')
        };

        // Tool-specific settings
        switch (selectedTool) {
            case 'video-generation':
                settings.model = videoOptions.model;
                settings.durationSeconds = videoOptions.duration;
                settings.resolution = videoOptions.resolution;
                settings.aspectRatio = videoOptions.aspectRatio || undefined;
                settings.generateAudio = videoOptions.generateAudio || undefined;
                break;
            case 'ad-generator':
                settings.language = adOptions.language;
                settings.title = adOptions.title || undefined;
                settings.subtitle = adOptions.subtitle || undefined;
                settings.ctaText = adOptions.ctaText || undefined;
                break;
            case 'upscaler': {
                const factorValue = getSelectedFactor(selections, 'factor');
                if (factorValue) {
                    settings.scaleFactor = factorValue;
                }
                break;
            }
        }

        return settings;
    }, [state, videoOptions, adOptions]);

    return {
        handleGenerate,
        handleStopGeneration,
        canGenerate,
        getGenerationSettings
    };
}
